import { useCallback, useEffect, useRef, useState } from "react";
import { ASHLEY_PROMPTS } from "../speech/ashleyPrompts";
import {
  cancelAshleySpeech,
  isAshleySpeechSupported,
  speakAshley,
} from "../speech/ashleySpeech";
import { POST_GREETING_LISTEN_DELAY_MS } from "../speech/voiceNavigationTiming";
import { useAshleyListening } from "../voice/recognition/useAshleyListening";
import {
  classifyWorkflowCommand,
  GUIDED_WORKFLOW_COMMAND,
} from "../forms/guidedWorkflowCommands";
import { matchDropdownOption } from "../forms/parseVoiceFieldValue";
import {
  registerGuidedWorkflow,
  unregisterGuidedWorkflow,
} from "../forms/guidedWorkflowRegistry";

export const START_SESSION_VOICE_PHASE = Object.freeze({
  SPEAKING: "speaking",
  LISTENING: "listening",
  WAITING: "waiting",
  CONFIRMING: "confirming",
});

const PROMPTS = Object.freeze({
  PROJECT_ASK: "Which project are you working on today?",
  PROJECT_NOT_FOUND:
    "I couldn't find that project. Please select it from the list below. If it isn't available, contact your manager.",
  PROJECT_TRY_AGAIN: "Okay, let's try again.",
  PROJECT_PERFECT: "Perfect.",
  PAYROLL_ASK: "What payroll type are you using today?",
  PAYROLL_TRY_AGAIN: "Okay, let's try again.",
  PAYROLL_PERFECT: "Perfect.",
  CLOSING_ASK:
    "I have everything I need. Would you like me to start your session now?",
  START_SUCCESS: "Your session has started. Have a great day.",
  MANUAL_ACK:
    "No problem. You can start your session whenever you're ready.",
});

function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function speakAndWait(message) {
  return new Promise((resolve) => {
    if (!isAshleySpeechSupported()) {
      resolve();
      return;
    }

    speakAshley(message, {
      onEnd: resolve,
      onError: resolve,
    });
  });
}

function scrollToVoiceField(fieldId) {
  if (!fieldId) {
    return;
  }

  const element = document.querySelector(`[data-voice-field="${fieldId}"]`);
  element?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function getOptionLabel(options, value) {
  return options.find((option) => option.value === value)?.label ?? "";
}

export function buildProjectConfirmMessage(label) {
  return `I have ${label}. Is that correct?`;
}

export function buildPayrollConfirmMessage(label) {
  return `I have ${label}. Is that correct?`;
}

function normalizeClosingTranscript(transcript) {
  return String(transcript || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function classifyStartSessionClosingResponse(transcript) {
  const normalized = normalizeClosingTranscript(transcript);
  if (!normalized) {
    return null;
  }

  if (
    normalized === "yes" ||
    normalized === "start" ||
    normalized === "start session" ||
    normalized === "go ahead" ||
    normalized === "thats right" ||
    normalized === "that s right" ||
    normalized.includes("start my session") ||
    normalized.includes("start the session")
  ) {
    return "start";
  }

  if (
    normalized === "no" ||
    normalized === "not yet" ||
    normalized.includes("do it manually") ||
    normalized.includes("ill do it manually") ||
    normalized.includes("i ll do it manually")
  ) {
    return "manual";
  }

  return null;
}

/**
 * Hardcoded Start Session voice MVP — project, payroll type, and session start.
 */
export function useStartSessionVoiceMvp({
  enabled,
  setSelectedProjectId,
  projectOptions,
  setSelectedPayrollType,
  payrollOptions,
  onStartSession,
  onComplete,
}) {
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState(START_SESSION_VOICE_PHASE.SPEAKING);
  const [statusMessage, setStatusMessage] = useState("");
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [interactionUI, setInteractionUI] = useState(null);

  const runningRef = useRef(false);
  const cancelledRef = useRef(false);
  const voiceSettingRef = useRef(false);
  const transcriptHandlerRef = useRef(null);
  const errorHandlerRef = useRef(null);
  const manualProjectResolverRef = useRef(null);
  const manualPayrollResolverRef = useRef(null);
  const confirmResolverRef = useRef(null);
  const listeningControlsRef = useRef(null);
  const projectOptionsRef = useRef(projectOptions);
  const payrollOptionsRef = useRef(payrollOptions);
  const sessionHandleRef = useRef({
    cancel: () => {},
    isActive: () => false,
  });

  projectOptionsRef.current = projectOptions;
  payrollOptionsRef.current = payrollOptions;

  const listenOnce = useCallback(() => {
    return new Promise((resolve, reject) => {
      transcriptHandlerRef.current = (transcript) => {
        transcriptHandlerRef.current = null;
        errorHandlerRef.current = null;
        resolve(transcript);
      };

      errorHandlerRef.current = (code) => {
        transcriptHandlerRef.current = null;
        errorHandlerRef.current = null;
        reject(new Error(code || "recognition_error"));
      };

      const started = listeningControlsRef.current?.startListening();
      if (started === false) {
        reject(new Error("unsupported"));
      }
    });
  }, []);

  const listening = useAshleyListening({
    onFinalTranscript: (transcript) => {
      transcriptHandlerRef.current?.(transcript);
    },
    onError: (code) => {
      errorHandlerRef.current?.(code);
    },
  });

  listeningControlsRef.current = listening;

  const stopSession = useCallback(({ cancelSpeech = true } = {}) => {
    cancelledRef.current = true;
    runningRef.current = false;
    if (cancelSpeech) {
      cancelAshleySpeech();
    }
    listeningControlsRef.current?.abortListening();
    transcriptHandlerRef.current = null;
    errorHandlerRef.current = null;
    manualProjectResolverRef.current = null;
    manualPayrollResolverRef.current = null;
    confirmResolverRef.current = null;
    unregisterGuidedWorkflow(sessionHandleRef.current);
    setIsActive(false);
    setActiveFieldId(null);
    setInteractionUI(null);
    setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
    setStatusMessage("");
  }, []);

  sessionHandleRef.current.cancel = stopSession;
  sessionHandleRef.current.isActive = () => runningRef.current;

  const resolveManualProject = useCallback((value) => {
    manualProjectResolverRef.current?.({ type: "manual", value });
    manualProjectResolverRef.current = null;
  }, []);

  const resolveManualPayroll = useCallback((value) => {
    manualPayrollResolverRef.current?.({ type: "manual", value });
    manualPayrollResolverRef.current = null;
  }, []);

  const waitForManualProject = useCallback(() => {
    return new Promise((resolve) => {
      manualProjectResolverRef.current = resolve;
    });
  }, []);

  const waitForManualPayroll = useCallback(() => {
    return new Promise((resolve) => {
      manualPayrollResolverRef.current = resolve;
    });
  }, []);

  const waitForConfirmation = useCallback(() => {
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
    });
  }, []);

  const resolveConfirmation = useCallback((accepted) => {
    confirmResolverRef.current?.(accepted);
    confirmResolverRef.current = null;
  }, []);

  const handleListenError = useCallback(async (code) => {
    if (code === "no-speech") {
      return "retry";
    }

    if (code === "unsupported") {
      setStatusMessage(ASHLEY_PROMPTS.UNSUPPORTED_BROWSER);
      return "stop";
    }

    if (code === "not-allowed") {
      setStatusMessage(ASHLEY_PROMPTS.MIC_DENIED);
      return "stop";
    }

    return "retry";
  }, []);

  const confirmValue = useCallback(
    async (message, summaryLabel, onReject) => {
      setPhase(START_SESSION_VOICE_PHASE.CONFIRMING);
      setStatusMessage(message);
      setInteractionUI({
        type: "confirm",
        message,
        summaryLabel,
      });

      await speakAndWait(message);

      if (cancelledRef.current || !runningRef.current) {
        return null;
      }

      await delay(POST_GREETING_LISTEN_DELAY_MS);

      while (runningRef.current && !cancelledRef.current) {
        setPhase(START_SESSION_VOICE_PHASE.LISTENING);
        setStatusMessage(message);

        const buttonPromise = waitForConfirmation().then((accepted) => ({
          type: "button",
          accepted,
        }));
        const listenPromise = listenOnce()
          .then((transcript) => ({ type: "voice", transcript }))
          .catch(async (error) => {
            const outcome = await handleListenError(error?.message);
            return { type: "error", outcome };
          });

        const result = await Promise.race([buttonPromise, listenPromise]);
        listeningControlsRef.current?.abortListening();

        if (result?.type === "button") {
          setInteractionUI(null);
          if (result.accepted) {
            return true;
          }
          onReject?.();
          return false;
        }

        if (result?.type === "error") {
          if (result.outcome === "stop") {
            return null;
          }
          continue;
        }

        const command = classifyWorkflowCommand(result.transcript);
        if (command === GUIDED_WORKFLOW_COMMAND.YES) {
          setInteractionUI(null);
          return true;
        }

        if (command === GUIDED_WORKFLOW_COMMAND.NO) {
          setInteractionUI(null);
          onReject?.();
          return false;
        }
      }

      return null;
    },
    [handleListenError, listenOnce, waitForConfirmation],
  );

  const collectProjectWithManual = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setPhase(START_SESSION_VOICE_PHASE.LISTENING);
      setStatusMessage(PROMPTS.PROJECT_ASK);

      const manualPromise = waitForManualProject();
      const listenPromise = listenOnce()
        .then((transcript) => ({ type: "voice", transcript }))
        .catch(async (error) => {
          const outcome = await handleListenError(error?.message);
          return { type: "error", outcome };
        });

      const result = await Promise.race([manualPromise, listenPromise]);
      listeningControlsRef.current?.abortListening();

      if (result?.type === "manual") {
        return { type: "manual" };
      }

      if (result?.type === "error") {
        if (result.outcome === "stop") {
          return null;
        }
        continue;
      }

      const matched = matchDropdownOption(
        result.transcript,
        projectOptionsRef.current,
      );
      if (matched) {
        voiceSettingRef.current = true;
        setSelectedProjectId(matched);
        voiceSettingRef.current = false;
        return { type: "matched", projectId: matched };
      }

      if (String(result.transcript || "").trim()) {
        setPhase(START_SESSION_VOICE_PHASE.WAITING);
        setStatusMessage(PROMPTS.PROJECT_NOT_FOUND);
        await speakAndWait(PROMPTS.PROJECT_NOT_FOUND);
        await delay(POST_GREETING_LISTEN_DELAY_MS);

        const manualResult = await waitForManualProject();
        if (manualResult?.type === "manual") {
          return { type: "manual" };
        }
        return null;
      }
    }

    return null;
  }, [
    handleListenError,
    listenOnce,
    setSelectedProjectId,
    waitForManualProject,
  ]);

  const collectPayrollWithManual = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setPhase(START_SESSION_VOICE_PHASE.LISTENING);
      setStatusMessage(PROMPTS.PAYROLL_ASK);

      const manualPromise = waitForManualPayroll();
      const listenPromise = listenOnce()
        .then((transcript) => ({ type: "voice", transcript }))
        .catch(async (error) => {
          const outcome = await handleListenError(error?.message);
          return { type: "error", outcome };
        });

      const result = await Promise.race([manualPromise, listenPromise]);
      listeningControlsRef.current?.abortListening();

      if (result?.type === "manual") {
        return { type: "manual" };
      }

      if (result?.type === "error") {
        if (result.outcome === "stop") {
          return null;
        }
        continue;
      }

      const matched = matchDropdownOption(
        result.transcript,
        payrollOptionsRef.current,
      );
      if (matched) {
        voiceSettingRef.current = true;
        setSelectedPayrollType(matched);
        voiceSettingRef.current = false;
        return { type: "matched", payrollType: matched };
      }

      if (String(result.transcript || "").trim()) {
        continue;
      }
    }

    return null;
  }, [
    handleListenError,
    listenOnce,
    setSelectedPayrollType,
    waitForManualPayroll,
  ]);

  const speakPromptAndPrepareListen = useCallback(async (prompt) => {
    setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
    setStatusMessage(prompt);
    await speakAndWait(prompt);

    if (cancelledRef.current || !runningRef.current) {
      return false;
    }

    await delay(POST_GREETING_LISTEN_DELAY_MS);
    return true;
  }, []);

  const runProjectStep = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setActiveFieldId("project");
      scrollToVoiceField("project");

      const ready = await speakPromptAndPrepareListen(PROMPTS.PROJECT_ASK);
      if (!ready) {
        return false;
      }

      const collectResult = await collectProjectWithManual();
      if (!collectResult) {
        return false;
      }

      if (collectResult.type === "manual") {
        setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.PROJECT_PERFECT);
        await speakAndWait(PROMPTS.PROJECT_PERFECT);
        setActiveFieldId(null);
        return true;
      }

      const label = getOptionLabel(
        projectOptionsRef.current,
        collectResult.projectId,
      );
      const confirmMessage = buildProjectConfirmMessage(label);
      const confirmed = await confirmValue(confirmMessage, label, () => {
        voiceSettingRef.current = true;
        setSelectedProjectId("");
        voiceSettingRef.current = false;
      });

      if (confirmed === true) {
        setActiveFieldId(null);
        return true;
      }

      if (confirmed === false) {
        setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.PROJECT_TRY_AGAIN);
        await speakAndWait(PROMPTS.PROJECT_TRY_AGAIN);
        continue;
      }

      return false;
    }

    return false;
  }, [
    collectProjectWithManual,
    confirmValue,
    setSelectedProjectId,
    speakPromptAndPrepareListen,
  ]);

  const runPayrollStep = useCallback(async () => {
    while (runningRef.current && !cancelledRef.current) {
      setActiveFieldId("payrollType");
      scrollToVoiceField("payrollType");

      const ready = await speakPromptAndPrepareListen(PROMPTS.PAYROLL_ASK);
      if (!ready) {
        return false;
      }

      const collectResult = await collectPayrollWithManual();
      if (!collectResult) {
        return false;
      }

      if (collectResult.type === "manual") {
        setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.PAYROLL_PERFECT);
        await speakAndWait(PROMPTS.PAYROLL_PERFECT);
        setActiveFieldId(null);
        return true;
      }

      const label = getOptionLabel(
        payrollOptionsRef.current,
        collectResult.payrollType,
      );
      const confirmMessage = buildPayrollConfirmMessage(label);
      const confirmed = await confirmValue(confirmMessage, label, () => {
        voiceSettingRef.current = true;
        setSelectedPayrollType("Standard");
        voiceSettingRef.current = false;
      });

      if (confirmed === true) {
        setActiveFieldId(null);
        return true;
      }

      if (confirmed === false) {
        setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.PAYROLL_TRY_AGAIN);
        await speakAndWait(PROMPTS.PAYROLL_TRY_AGAIN);
        continue;
      }

      return false;
    }

    return false;
  }, [
    collectPayrollWithManual,
    confirmValue,
    setSelectedPayrollType,
    speakPromptAndPrepareListen,
  ]);

  const runClosingStep = useCallback(async () => {
    setActiveFieldId(null);

    const ready = await speakPromptAndPrepareListen(PROMPTS.CLOSING_ASK);
    if (!ready) {
      return false;
    }

    while (runningRef.current && !cancelledRef.current) {
      setPhase(START_SESSION_VOICE_PHASE.LISTENING);
      setStatusMessage(PROMPTS.CLOSING_ASK);

      let transcript;
      try {
        transcript = await listenOnce();
      } catch (error) {
        const outcome = await handleListenError(error?.message);
        if (outcome === "stop") {
          return false;
        }
        continue;
      }

      if (classifyWorkflowCommand(transcript) === GUIDED_WORKFLOW_COMMAND.CANCEL) {
        stopSession();
        return false;
      }

      const decision = classifyStartSessionClosingResponse(transcript);
      if (decision === "start") {
        const started = (await onStartSession?.()) === true;
        if (!started) {
          continue;
        }

        setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.START_SUCCESS);
        await speakAndWait(PROMPTS.START_SUCCESS);
        return true;
      }

      if (decision === "manual") {
        setPhase(START_SESSION_VOICE_PHASE.SPEAKING);
        setStatusMessage(PROMPTS.MANUAL_ACK);
        await speakAndWait(PROMPTS.MANUAL_ACK);
        return true;
      }
    }

    return false;
  }, [handleListenError, listenOnce, onStartSession, speakPromptAndPrepareListen, stopSession]);

  const runSession = useCallback(async () => {
    if (runningRef.current) {
      return;
    }

    cancelledRef.current = false;
    runningRef.current = true;
    setIsActive(true);
    registerGuidedWorkflow(sessionHandleRef.current);

    const projectOk = await runProjectStep();
    if (!projectOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const payrollOk = await runPayrollStep();
    if (!payrollOk || cancelledRef.current) {
      stopSession();
      return;
    }

    const closingOk = await runClosingStep();
    if (!closingOk || cancelledRef.current) {
      stopSession();
      return;
    }

    stopSession({ cancelSpeech: false });
    onComplete?.();
  }, [onComplete, runClosingStep, runPayrollStep, runProjectStep, stopSession]);

  const runSessionRef = useRef(runSession);
  runSessionRef.current = runSession;

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      if (!runningRef.current) {
        runSessionRef.current();
      }
    }, 600);

    return () => {
      window.clearTimeout(timer);
    };
  }, [enabled]);

  useEffect(() => () => stopSession(), [stopSession]);

  const notifyFieldChange = useCallback(
    (fieldId, value) => {
      if (!runningRef.current || voiceSettingRef.current || !value) {
        return;
      }

      if (fieldId === "project") {
        resolveManualProject(value);
      } else if (fieldId === "payrollType") {
        resolveManualPayroll(value);
      }
    },
    [resolveManualPayroll, resolveManualProject],
  );

  const notifyFieldBlur = useCallback(() => {}, []);

  const confirmStepYes = useCallback(() => {
    resolveConfirmation(true);
  }, [resolveConfirmation]);

  const confirmStepNo = useCallback(() => {
    resolveConfirmation(false);
  }, [resolveConfirmation]);

  return {
    isActive,
    phase,
    statusMessage,
    activeFieldId,
    interactionUI,
    notifyFieldChange,
    notifyFieldBlur,
    confirmStepYes,
    confirmStepNo,
    cancel: stopSession,
  };
}

export {
  PROMPTS as START_SESSION_VOICE_PROMPTS,
  getOptionLabel,
};
